import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { 
  ArrowLeft, Mail, Building, Globe, CreditCard, 
  Calendar, CheckCircle, Clock, ShieldCheck, History 
} from 'lucide-react';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fullData, setFullData] = useState(null);
  const [activeTab, setActiveTab] = useState('credits'); 
  const [loading, setLoading] = useState(true);
  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    const fetchFullDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseurl}getUserById/${id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        if (response.data.success) {
          setFullData(response.data);
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Oops...', text: 'Failed to load user profile.' });
      } finally {
        setLoading(false);
      }
    };
    fetchFullDetails();
  }, [id, adminToken]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, { 
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
      <div className="spinner-border text-primary" role="status"></div>
    </div>
  );

  if (!fullData) return <div className="p-5 text-center">User not found.</div>;

  const { user, subscriptionHistory, creditHistory } = fullData;

  return (
     <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex' }}>
        {/* <SideMenu /> */}
        <div className="profile-content-container UD-page-wrapper p-2" style={{ flex: 1, overflow: 'auto' }}>
          
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <button onClick={() => navigate(-1)} className="btn btn-link text-decoration-none p-0 mb-2 text-muted small">
                <ArrowLeft size={16} className="me-1" /> Back to Users
              </button>
              <h2 className="fw-bold m-0" style={{ color: '#1B2559' }}>Management Console</h2>
            </div>
            <div className="badge bg-white shadow-sm border text-dark p-2 px-3 rounded-3">
              <Clock size={14} className="me-2 text-primary" />
              Last Synced: {new Date(fullData.meta.fetchedAt).toLocaleTimeString()}
            </div>
          </div>

          <div className="row g-4">
            {/* PROFILE SIDEBAR */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm p-4 text-center mb-4" style={{ borderRadius: '20px' }}>
                <div className="mx-auto mb-3 avatar-main">
                  {user.first_name[0]}{user.last_name[0]}
                </div>
                <h4 className="fw-bold mb-1">{user.first_name} {user.last_name}</h4>
                <p className="text-muted small mb-3">{user.email}</p>
                <div className="d-flex justify-content-center gap-2">
                  <span className={`badge-custom ${user.subscription_status}`}>{user.subscription_status}</span>
                  <span className="badge-custom plan text-capitalize">{user.master_plan_type}</span>
                </div>
              </div>

              {/* AUDIT DETAILS */}
              <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
                <h6 className="fw-bold mb-3 border-bottom pb-2">Technical Audit</h6>
                <div className="audit-item mb-3">
                    <small className="text-muted d-block">Account Created</small>
                    <span className="fw-500 small">{formatDate(user.account_created)}</span>
                </div>
                <div className="audit-item mb-3">
                    <small className="text-muted d-block">Last Profile Update</small>
                    <span className="fw-500 small">{formatDate(user.profile_updated)}</span>
                </div>
                <div className="audit-item mb-3">
                    <small className="text-muted d-block">Wallet Sync</small>
                    <span className="fw-500 small">{formatDate(user.wallet_updated)}</span>
                </div>
                <div className="d-flex align-items-center gap-2 mt-2">
                   {user.agreed_terms ? <ShieldCheck size={18} className="text-success" /> : <Clock size={18} className="text-warning" />}
                   <span className="small fw-bold">{user.agreed_terms ? 'Legal Terms Accepted' : 'Terms Pending'}</span>
                </div>
              </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="col-lg-8">
              {/* FINANCE CARDS */}
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="stat-card">
                    <div className="stat-icon bg-soft-blue"><CreditCard size={20} /></div>
                    <div><small className="text-muted">Balance</small><h4 className="fw-bold mb-0">${user.balance}</h4></div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="stat-card">
                    <div className="stat-icon bg-soft-green"><CheckCircle size={20} /></div>
                    <div><small className="text-muted">Lifetime Earned</small><h4 className="fw-bold mb-0 text-success">${user.total_earned}</h4></div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="stat-card">
                    <div className="stat-icon bg-soft-orange"><Calendar size={20} /></div>
                    <div><small className="text-muted">Plan Expiry</small><h4 className="fw-bold mb-0">{new Date(user.subscription_expiry).toLocaleDateString()}</h4></div>
                  </div>
                </div>
              </div>

              {/* TABS FOR HISTORY */}
              <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                <div className="card-header bg-white border-0 pt-3 px-4">
                  <div className="d-flex gap-4 border-bottom">
                    <button className={`tab-btn ${activeTab === 'credits' ? 'active' : ''}`} onClick={() => setActiveTab('credits')}>Credit Ledger</button>
                    <button className={`tab-btn ${activeTab === 'subs' ? 'active' : ''}`} onClick={() => setActiveTab('subs')}>Subscription History</button>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    {activeTab === 'credits' ? (
                      <table className="table table-hover align-middle m-0">
                        <thead className="bg-light small text-muted">
                          <tr><th className="ps-4">DATE</th><th>TYPE</th><th>DESCRIPTION</th><th className="text-end pe-4">AMOUNT</th></tr>
                        </thead>
                        <tbody>
                          {creditHistory.map(log => (
                            <tr key={log.id}>
                              <td className="ps-4 small">{formatDate(log.log_date)}</td>
                              <td><span className="badge-type">{log.transaction_type}</span></td>
                              <td className="small text-muted">{log.description}</td>
                              <td className="text-end pe-4 fw-bold text-success">+${log.amount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <table className="table table-hover align-middle m-0">
                        <thead className="bg-light small text-muted">
                          <tr><th className="ps-4">PROCESSED</th><th>EVENT</th><th>PLAN</th><th>PERIOD</th><th className="text-end pe-4">PAID</th></tr>
                        </thead>
                        <tbody>
                          {subscriptionHistory.map(sub => (
                            <tr key={sub.id}>
                              <td className="ps-4 small">{formatDate(sub.processed_at)}</td>
                              <td className="text-capitalize small fw-bold">{sub.transaction_type.replace('_', ' ')}</td>
                              <td><span className="badge-custom plan py-1">{sub.plan_type}</span></td>
                              <td className="text-xs text-muted">{new Date(sub.period_start).toLocaleDateString()} - {new Date(sub.period_end).toLocaleDateString()}</td>
                              <td className="text-end pe-4 fw-bold">${sub.amount_paid}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .avatar-main { width: 90px; height: 90px; background: #4318FF; color: white; border-radius: 25px; display: flex; align-items: center; justify-content: center; font-size: 28px; font-weight: bold; }
        .badge-custom { padding: 4px 12px; border-radius: 8px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .badge-custom.trialing { background: #FFF3E0; color: #FF9800; }
        .badge-custom.plan { background: #F4F7FE; color: #4318FF; }
        .badge-type { background: #F4F7FE; color: #707EAE; padding: 2px 8px; border-radius: 5px; font-size: 10px; font-weight: bold; text-transform: uppercase; }
        .stat-card { background: white; padding: 18px; border-radius: 18px; display: flex; align-items: center; gap: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.02); }
        .stat-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .bg-soft-blue { background: #E1F5FE; color: #03A9F4; }
        .bg-soft-green { background: #E8F5E9; color: #4CAF50; }
        .bg-soft-orange { background: #FFF3E0; color: #FF9800; }
        .tab-btn { background: none; border: none; padding: 10px 0; color: #A3AED0; font-weight: 600; font-size: 14px; position: relative; }
        .tab-btn.active { color: #4318FF; }
        .tab-btn.active::after { content: ''; position: absolute; bottom: -1px; left: 0; width: 100%; height: 2px; background: #4318FF; }
        .fw-500 { font-weight: 500; }
        .text-xs { font-size: 11px; }
      `}</style>
    </div>
  );
};

export default UserDetail;